import React, { useEffect, useState, useRef, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import interactionPlugin from "@fullcalendar/interaction";
import dayGridPlugin from "@fullcalendar/daygrid";
import esLocale from "@fullcalendar/core/locales/es";
import useStore from "../../../useStore";
import { useToast } from "../../../Contexts/Toast/toast.provider";
import Loader from "../../Elements/Loader/Loader";
import "./calendarioVacaciones.css";
import * as XLSX from "xlsx";

const colorByTipo = {
    vacaciones: "#90EE90",
    permisos: "#FFD700",
    incapacidades: "#ADD8E6",
    faltas: "#FFA07A",
};

const startOfToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
};

const formatLocalDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const parseDateTime = (dateTimeStr) => new Date(dateTimeStr.replace(" ", "T"));

const isWeekday = (date) => {
    const day = date.getDay();
    return day !== 0;
};

const getDatesInRangeExclusive = (start, end) => {
    const dates = [];
    const current = new Date(start);
    while (current < end) {
        dates.push(new Date(current));
        current.setDate(current.getDate() + 1);
    }
    return dates;
};

function getTurno(entradaStr) {
    const entrada = typeof entradaStr === "string" ? parseDateTime(entradaStr) : entradaStr;
    const first = entrada.getHours() * 60 + entrada.getMinutes();
    if (first >= 7 * 60 + 30 && first < 8 * 60 + 30) return "Mixto";
    if (first >= 19 * 60 + 30 && first < 20 * 60 + 30) return "Mixto";
    if (first >= 5 * 60 && first < 6 * 60) return "1";
    if (first >= 13 * 60 && first < 14 * 60) return "2";
    if (first >= 21 * 60 && first < 22 * 60) return "3";
    if (first < 14 * 60) return "1";
    if (first < 22 * 60) return "2";
    return "3";
}

function getHorarioInicioTurno(turno, entrada) {
    const start = new Date(entrada);
    if (turno === "Mixto") {
        start.setHours(8, 30, 0, 0);
    } else if (turno === "1") {
        start.setHours(6, 0, 0, 0);
    } else if (turno === "2") {
        start.setHours(14, 0, 0, 0);
    } else {
        if (entrada.getHours() < 6) start.setDate(start.getDate() - 1);
        start.setHours(22, 0, 0, 0);
    }
    return start;
}

function analyzeEntradaChecada(eventDatetime) {
    const entrada = parseDateTime(eventDatetime);
    const turno = getTurno(eventDatetime);
    const horaInicio = getHorarioInicioTurno(turno, entrada);
    const diffMs = entrada.getTime() - horaInicio.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes <= 5) return { turno, estatus: "asistencia", minutesLate: diffMinutes < 0 ? 0 : diffMinutes };
    if (diffMinutes >= 6 && diffMinutes <= 10) return { turno, estatus: "retardo", minutesLate: diffMinutes };
    return { turno, estatus: "falta", minutesLate: diffMinutes };
}

function isEmployeeActiveOnDate(emp, dateStr) {
    const periodos = emp?.datosPuesto?.periodos || [];
    const target = parseDate(dateStr);
    return periodos.some((p) => {
        if (!p?.fechaIngreso) return false;
        const ingreso = parseDate(p.fechaIngreso);
        const egreso = p.fechaEgreso ? parseDate(p.fechaEgreso) : null;
        if (target < ingreso) return false;
        if (egreso && target > egreso) return false;
        return true;
    });
}

const parseDate = (dateStr) => new Date(dateStr + "T00:00:00");
const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
};
const subtractDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() - days);
    return d;
};
const formatISODate = (date) => date.toISOString().split("T")[0];

const CHECADOR_CQZ = "checador_CQZ7233361670";
const CHECADOR_JJA = "checador_JJA1241400074";

export default function CalendarioVacaciones() {
    const {
        users,
        fetchEmpleados,
        loading: loadingEmpleados,
        error,
        fetchChecador,
        checador,
        loadingChecador
    } = useStore();
    const showToast = useToast();

    const [events, setEvents] = useState([]);
    const [selectedDateEvents, setSelectedDateEvents] = useState([]);
    const [clickedDate, setClickedDate] = useState(null);
    const prevErrorRef = useRef(null);
    const [visibleRange, setVisibleRange] = useState(null);
    const [exportMes, setExportMes] = useState("");

    useEffect(() => {
        if (users.length === 0) fetchEmpleados();
    }, [users, fetchEmpleados]);

    useEffect(() => {
        if (checador.length === 0) fetchChecador();
    }, [checador.length, fetchChecador]);

    useEffect(() => {
        if (error && error !== prevErrorRef.current) {
            const message = error instanceof Error ? error.message : error?.error || "Error desconocido";
            showToast("error", message);
            prevErrorRef.current = error;
        }
    }, [error, showToast]);

    // Días con actividad en CQZ — disponible globalmente en el componente
    const diasConRegistroCQZ = useMemo(() => new Set(
        (checador || [])
            .filter(c => c.device_id === CHECADOR_CQZ && c?.parsed?.event_datetime)
            .map(c => c.parsed.event_datetime.slice(0, 10))
    ), [checador]);

    // Meses disponibles para exportar (solo los que tienen checadas)
    const mesesDisponibles = useMemo(() => {
        const meses = new Set();
        (checador || []).forEach(c => {
            const dt = c?.parsed?.event_datetime;
            if (dt) meses.add(dt.slice(0, 7));
        });
        return Array.from(meses).sort();
    }, [checador]);

    useEffect(() => {
        if (!users || users.length === 0) {
            setEvents([]);
            return;
        }

        const allEvents = [];

        const checadasMap = new Map();
        (checador || []).forEach((ch) => {
            const employeeNumber = String(ch?.parsed?.employee_number || "").trim();
            const eventDatetime = ch?.parsed?.event_datetime;
            if (!employeeNumber || !eventDatetime) return;
            const dateKey = eventDatetime.slice(0, 10);
            const mapKey = `${employeeNumber}_${dateKey}`;
            if (!checadasMap.has(mapKey)) checadasMap.set(mapKey, []);
            checadasMap.get(mapKey).push(ch);
        });

        users.forEach((emp) => {
            const nombre = `${emp.datosPersonales?.nombre || ""} ${emp.datosPersonales?.apellidoPaterno || ""}`.trim();
            const puesto = emp.datosPuesto || {};
            const numeroEmpleado = String(emp.numeroEmpleado || "").trim();

            const justifiedDates = new Set();
            const existingFaltas = new Set();

            (puesto.vacacionesTomadas || []).forEach((vac) => {
                (vac.fechas || []).forEach((fecha) => {
                    justifiedDates.add(fecha);
                    allEvents.push({
                        title: `${nombre} (vacaciones)`,
                        start: fecha,
                        allDay: true,
                        backgroundColor: colorByTipo.vacaciones,
                        borderColor: colorByTipo.vacaciones,
                        textColor: "#000",
                        extendedProps: { nombre, tipo: "vacaciones", descripcion: `Folio: ${vac.folio || "N/A"} (${vac.anio || ""})` },
                    });
                });
            });

            (puesto.permisos || []).forEach((permiso) => {
                if (permiso.fecha) {
                    justifiedDates.add(permiso.fecha);
                    allEvents.push({
                        title: `${nombre} (permiso)`,
                        start: permiso.fecha,
                        allDay: true,
                        backgroundColor: colorByTipo.permisos,
                        borderColor: colorByTipo.permisos,
                        textColor: "#000",
                        extendedProps: { nombre, tipo: "permiso", descripcion: permiso.motivo || "" },
                    });
                }
            });

            (puesto.incapacidades || []).forEach((inc) => {
                if (inc.fechaInicio && inc.fechaFin) {
                    const startDate = parseDate(inc.fechaInicio);
                    const endExclusive = addDays(parseDate(inc.fechaFin), 1);
                    getDatesInRangeExclusive(startDate, endExclusive).forEach((d) => {
                        justifiedDates.add(formatLocalDate(d));
                    });
                    allEvents.push({
                        title: `${nombre} (incapacidad)`,
                        start: startDate,
                        end: endExclusive,
                        allDay: true,
                        backgroundColor: colorByTipo.incapacidades,
                        borderColor: colorByTipo.incapacidades,
                        textColor: "#000",
                        extendedProps: { nombre, tipo: "incapacidad", descripcion: `${inc.motivo || ""} (Folio IMSS: ${inc.folioIMSS || "N/A"})` },
                    });
                }
            });

            (puesto.faltas || []).forEach((falta) => {
                if (falta.fecha) {
                    existingFaltas.add(falta.fecha);
                    allEvents.push({
                        title: `${nombre} (falta)`,
                        start: falta.fecha,
                        allDay: true,
                        backgroundColor: colorByTipo.faltas,
                        borderColor: colorByTipo.faltas,
                        textColor: "#000",
                        extendedProps: { nombre, tipo: "falta", descripcion: `${falta.motivo || ""}${falta.justificada ? " (justificada)" : " (no justificada)"}` },
                    });
                }
            });

            if (visibleRange?.start && visibleRange?.end) {
                const visibleDates = getDatesInRangeExclusive(visibleRange.start, visibleRange.end);

                visibleDates.forEach((dateObj) => {
                    const dateStr = formatLocalDate(dateObj);
                    const today = startOfToday();

                    if (dateObj > today) return;
                    if (!isWeekday(dateObj)) return;
                    if (!isEmployeeActiveOnDate(emp, dateStr)) return;
                    if (justifiedDates.has(dateStr)) return;
                    if (existingFaltas.has(dateStr)) return;

                    const checadasCQZ = (checador || []).filter(
                        c =>
                            c.device_id === CHECADOR_CQZ &&
                            c?.parsed?.event_datetime?.slice(0, 10) === dateStr &&
                            String(c?.parsed?.employee_number || "").trim() === numeroEmpleado
                    );

                    const checadasJJA = (checador || []).filter(
                        c =>
                            c.device_id === CHECADOR_JJA &&
                            c?.parsed?.event_datetime?.slice(0, 10) === dateStr &&
                            String(c?.parsed?.employee_number || "").trim() === numeroEmpleado
                    );

                    let debeMarcarFalta = false;
                    let descripcion = "Falta detectada automáticamente por checador.";

                    if (checadasCQZ.length > 0) {
                        return;
                    }

                    if (checadasJJA.length > 0) {
                        const entradas = checadasJJA
                            .filter(c => c?.parsed?.event_type === "entry")
                            .sort((a, b) => parseDateTime(a.parsed.event_datetime) - parseDateTime(b.parsed.event_datetime));
                        const salidas = checadasJJA.filter(c => c?.parsed?.event_type === "exit");

                        if (entradas.length === 0 && salidas.length > 0) {
                            debeMarcarFalta = true;
                            descripcion = "Solo tiene checadas de salida; no registró entrada.";
                        } else if (entradas.length > 0) {
                            const analysis = analyzeEntradaChecada(entradas[0].parsed.event_datetime);
                            if (analysis.estatus === "falta") {
                                debeMarcarFalta = true;
                                descripcion = `Primera entrada tardía para turno ${analysis.turno}. ${analysis.minutesLate} minutos después de la hora de entrada.`;
                            }
                        }
                    } else {
                        if (diasConRegistroCQZ.has(dateStr)) {
                            debeMarcarFalta = true;
                            descripcion = "No tiene checadas registradas en checador CEDIS.";
                        } else {
                            debeMarcarFalta = true;
                            descripcion = "No tiene checadas registradas en el día.";
                        }
                    }

                    if (debeMarcarFalta) {
                        allEvents.push({
                            title: `${nombre} (falta)`,
                            start: dateStr,
                            allDay: true,
                            backgroundColor: colorByTipo.faltas,
                            borderColor: colorByTipo.faltas,
                            textColor: "#000",
                            extendedProps: { nombre, tipo: "falta", descripcion },
                        });
                    }
                });
            }
        });

        setEvents(allEvents);
    }, [users, checador, visibleRange, diasConRegistroCQZ]);

    const handleExportar = () => {
        console.log("exportMes value333:", exportMes);
        if (!exportMes) return;
        const periodoKey = exportMes;
        console.log("periodoKey:", periodoKey);
        const filas = [];

        users.forEach((emp) => {
            const nombre = `${emp.datosPersonales?.nombre || ""} ${emp.datosPersonales?.apellidoPaterno || ""}`.trim();
            const numeroEmpleado = String(emp.numeroEmpleado || "").trim();
            const puesto = emp.datosPuesto || {};

            const justifiedDates = new Set();
            const existingFaltas = new Set();

            (puesto.vacacionesTomadas || []).forEach(vac => (vac.fechas || []).forEach(f => justifiedDates.add(f)));
            (puesto.permisos || []).forEach(p => p.fecha && justifiedDates.add(p.fecha));
            (puesto.incapacidades || []).forEach(inc => {
                if (inc.fechaInicio && inc.fechaFin) {
                    const start = parseDate(inc.fechaInicio);
                    const end = addDays(parseDate(inc.fechaFin), 1);
                    getDatesInRangeExclusive(start, end).forEach(d => justifiedDates.add(formatLocalDate(d)));
                }
            });
            (puesto.faltas || []).forEach(f => f.fecha && existingFaltas.add(f.fecha));

            const [anio, mes] = periodoKey.split("-").map(Number);
            const diasEnMes = new Date(anio, mes, 0).getDate();

            for (let dia = 1; dia <= diasEnMes; dia++) {
                const dateStr = `${periodoKey}-${String(dia).padStart(2, "0")}`;
                const dateObj = parseDate(dateStr);
                const today = startOfToday();

                if (dateObj > today) continue;
                if (!isWeekday(dateObj)) continue;
                if (!isEmployeeActiveOnDate(emp, dateStr)) continue;

                if (justifiedDates.has(dateStr)) {
                    filas.push({ Empleado: nombre, Fecha: dateStr, Tipo: "Justificado", "Descripción": "Vacaciones / Permiso / Incapacidad" });
                    continue;
                }

                if (existingFaltas.has(dateStr)) {
                    filas.push({ Empleado: nombre, Fecha: dateStr, Tipo: "Falta", "Descripción": "Falta registrada manualmente" });
                    continue;
                }

                const checadasCQZ = (checador || []).filter(
                    c => c.device_id === CHECADOR_CQZ &&
                        c?.parsed?.event_datetime?.slice(0, 10) === dateStr &&
                        String(c?.parsed?.employee_number || "").trim() === numeroEmpleado
                );

                const checadasJJA = (checador || []).filter(
                    c => c.device_id === CHECADOR_JJA &&
                        c?.parsed?.event_datetime?.slice(0, 10) === dateStr &&
                        String(c?.parsed?.employee_number || "").trim() === numeroEmpleado
                );

                if (checadasCQZ.length > 0) {
                    filas.push({ Empleado: nombre, Fecha: dateStr, Tipo: "Asistencia", "Descripción": "Checó en CEDIS" });
                    continue;
                }

                if (checadasJJA.length > 0) {
                    const entradas = checadasJJA
                        .filter(c => c?.parsed?.event_type === "entry")
                        .sort((a, b) => parseDateTime(a.parsed.event_datetime) - parseDateTime(b.parsed.event_datetime));
                    const salidas = checadasJJA.filter(c => c?.parsed?.event_type === "exit");

                    if (entradas.length === 0 && salidas.length > 0) {
                        filas.push({ Empleado: nombre, Fecha: dateStr, Tipo: "Falta", "Descripción": "Solo tiene checadas de salida; no registró entrada." });
                    } else if (entradas.length > 0) {
                        const analysis = analyzeEntradaChecada(entradas[0].parsed.event_datetime);
                        if (analysis.estatus === "falta") {
                            filas.push({ Empleado: nombre, Fecha: dateStr, Tipo: "Falta", "Descripción": `Entrada tardía turno ${analysis.turno}. ${analysis.minutesLate} min después.` });
                        } else if (analysis.estatus === "retardo") {
                            filas.push({ Empleado: nombre, Fecha: dateStr, Tipo: "Retardo", "Descripción": `${analysis.minutesLate} min tarde, turno ${analysis.turno}.` });
                        } else {
                            filas.push({ Empleado: nombre, Fecha: dateStr, Tipo: "Asistencia", "Descripción": `Checó en PLANTA, turno ${analysis.turno}.` });
                        }
                    } else {
                        filas.push({ Empleado: nombre, Fecha: dateStr, Tipo: "Asistencia", "Descripción": "Checó en PLANTA." });
                    }
                    continue;
                }

                if (diasConRegistroCQZ.has(dateStr)) {
                    filas.push({ Empleado: nombre, Fecha: dateStr, Tipo: "Falta", "Descripción": "Sin registro en checador CEDIS." });
                } else {
                    filas.push({ Empleado: nombre, Fecha: dateStr, Tipo: "Falta", "Descripción": "Sin checadas registradas en el día." });
                }
            }
        });

        if (filas.length === 0) {
            alert("No hay datos para el período seleccionado.");
            return;
        }

        const ws = XLSX.utils.json_to_sheet(filas);
        ws["!cols"] = [{ wch: 30 }, { wch: 14 }, { wch: 14 }, { wch: 55 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, `Asistencias ${periodoKey}`);
        XLSX.writeFile(wb, `asistencias_${periodoKey}.xlsx`);
    };

    const handleDateClick = (arg) => {
        const clicked = arg.dateStr;
        const clickedDateObj = new Date(clicked + "T00:00:00");
        const filtered = events.filter((ev) => {
            const startDate = ev.start instanceof Date ? ev.start : parseDate(ev.start);
            if (ev.end) {
                const endDate = ev.end instanceof Date ? ev.end : parseDate(ev.end);
                return clickedDateObj >= startDate && clickedDateObj < endDate;
            } else {
                return formatISODate(startDate) === formatISODate(clickedDateObj);
            }
        });
        setClickedDate(clicked);
        setSelectedDateEvents(filtered);
    };

    const getDisplayRange = (ev, tipo) => {
        const start = ev.start instanceof Date ? ev.start : parseDate(ev.start);
        let end;
        if (tipo === "incapacidad" && ev.end) {
            const rawEnd = ev.end instanceof Date ? ev.end : parseDate(ev.end);
            end = subtractDays(rawEnd, 1);
        } else if (ev.end) {
            end = ev.end instanceof Date ? ev.end : parseDate(ev.end);
        } else {
            end = start;
        }
        return { startStr: formatISODate(start), endStr: formatISODate(end) };
    };

    if (loadingEmpleados || loadingChecador) return <Loader />;

    return (
        <div className="vacaciones-calendar container-mh">
            <h1 className="mb-5">Calendario de Empleados General</h1>

            {/* Exportar por período */}
            <div className="d-flex align-items-end gap-3 mb-4 flex-wrap">
                <div>
                    <label className="form-label mb-1" style={{ fontSize: "0.85rem" }}>Período</label>
                    <select
                        className="form-select form-select-sm"
                        value={exportMes}
                        onChange={e => setExportMes(e.target.value)}
                    >
                        <option value="">-- Selecciona período --</option>
                        {mesesDisponibles.map(md => {
                            const [anio, mes] = md.split("-");
                            const mesNum = parseInt(mes, 10) - 1; // 0-indexed para JS
                            const label = new Date(2000, mesNum, 1).toLocaleString("es-MX", { month: "long" });
                            return (
                                <option key={md} value={md}>
                                    {label.charAt(0).toUpperCase() + label.slice(1)} {anio}
                                </option>
                            );
                        })}
                    </select>
                </div>
                <button
                    className="btn btn-success btn-sm"
                    onClick={handleExportar}
                    disabled={!exportMes}
                >
                    Exportar Excel
                </button>
            </div>

            <div className="legend d-flex gap-3 flex-wrap mb-3">
                {Object.entries(colorByTipo).map(([tipo, color]) => (
                    <div className="legend-item d-flex align-items-center" key={tipo}>
                        <span className="legend-color" style={{ backgroundColor: color }}></span>
                        {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                    </div>
                ))}
            </div>

            <FullCalendar
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                locale={esLocale}
                headerToolbar={{ left: "prev,next today", center: "title", right: "" }}
                events={events}
                dateClick={handleDateClick}
                datesSet={(arg) => setVisibleRange({ start: arg.start, end: arg.end })}
                height="auto"
                dayMaxEventRows={4}
                dayMaxEvents={true}
                moreLinkContent={(args) => `+${args.num} más`}
                eventDisplay="block"
            />

            {clickedDate && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Eventos del día: {clickedDate}</h5>
                                <button type="button" className="btn-close" onClick={() => setClickedDate(null)}></button>
                            </div>
                            <div className="modal-body">
                                {selectedDateEvents.length > 0 ? (
                                    <>
                                        {["vacaciones", "permiso", "incapacidad", "falta"].map((tipo) => {
                                            const eventosPorTipo = selectedDateEvents.filter(ev => ev.extendedProps.tipo === tipo);
                                            if (eventosPorTipo.length === 0) return null;
                                            const emoji = { vacaciones: "🟢", permiso: "🟡", incapacidad: "🔵", falta: "🟠" }[tipo];
                                            return (
                                                <div key={tipo} className="mb-3">
                                                    <h5>{emoji} {tipo.charAt(0).toUpperCase() + tipo.slice(1)}s</h5>
                                                    <ul className="list-group">
                                                        {eventosPorTipo.map((ev, i) => {
                                                            const { nombre, descripcion } = ev.extendedProps;
                                                            const { startStr: start, endStr: end } = getDisplayRange(ev, tipo);
                                                            let detalle = "";
                                                            switch (tipo) {
                                                                case "vacaciones": detalle = `${nombre} solicitó vacaciones. ${descripcion}`; break;
                                                                case "permiso": detalle = `${nombre} solicitó un permiso. Motivo: ${descripcion}`; break;
                                                                case "incapacidad": detalle = `${nombre} solicitó una incapacidad del ${start} al ${end}. Detalle: ${descripcion}`; break;
                                                                case "falta": detalle = `${nombre} tuvo una falta. Detalle: ${descripcion}`; break;
                                                                default: detalle = `${nombre} tiene un evento.`;
                                                            }
                                                            return <li key={i} className="list-group-item">{detalle}</li>;
                                                        })}
                                                    </ul>
                                                </div>
                                            );
                                        })}
                                    </>
                                ) : (
                                    <p>No hay eventos registrados para este día.</p>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setClickedDate(null)}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}